import { Injectable, inject } from '@angular/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { finalize, from, Observable, tap } from 'rxjs';
import type { Card } from '../../interfaces/ICardItem';
import { TileService } from '../../services/tile.service';
import { AddReport, DeleteReport, LoadReports, UpdateReport } from './reports.actions';

export interface ReportsStateModel {
    isLoading: boolean;
    reports: Card[];
    loadedLanguage: string | null;
}

@State<ReportsStateModel>({
    name: 'reportsState',
    defaults: {
        isLoading: false,
        reports: [],
        loadedLanguage: null
    }
})
@Injectable()
export class ReportsState {
    private readonly tileService = inject(TileService);

    @Selector()
    static isLoading(state: ReportsStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static reports(state: ReportsStateModel): Card[] {
        return state.reports;
    }

    @Selector()
    static loadedLanguage(state: ReportsStateModel): string | null {
        return state.loadedLanguage;
    }

    @Action(LoadReports)
    public loadReports(ctx: unknown, action: { payload: { lang: string } }): Observable<Card[]> {
        const stateContext = ctx as StateContext<ReportsStateModel>;
        const { payload } = action;

        stateContext.patchState({ isLoading: true });

        return from(this.tileService.getCardsByType('reports', payload.lang)).pipe(
            tap(reports => {
                stateContext.patchState({
                    reports,
                    loadedLanguage: payload.lang
                });
            }),
            finalize(() => {
                stateContext.patchState({ isLoading: false });
            })
        );
    }

    @Action(AddReport)
    public addReport(ctx: unknown, action: { payload: { report: Card } }): void {
        const stateContext = ctx as StateContext<ReportsStateModel>;
        const state = stateContext.getState();
        stateContext.patchState({
            reports: [...state.reports, action.payload.report]
        });
    }

    @Action(UpdateReport)
    public updateReport(ctx: unknown, action: { payload: { report: Card } }): void {
        const stateContext = ctx as StateContext<ReportsStateModel>;
        const state = stateContext.getState();
        stateContext.patchState({
            reports: state.reports.map(r => (r.id === action.payload.report.id ? action.payload.report : r))
        });
    }

    @Action(DeleteReport)
    public deleteReport(ctx: unknown, action: { payload: { id: number } }): void {
        const stateContext = ctx as StateContext<ReportsStateModel>;
        const state = stateContext.getState();
        stateContext.patchState({
            reports: state.reports.filter(r => r.id !== action.payload.id)
        });
    }
}
